
import { useState, useEffect } from 'react';
import { DealerCreditService, DealerCreditTransaction, DealerCreditPackage } from '@/services/dealerCreditService';

export interface UseDealerCreditsReturn {
  credits: number;
  isLoading: boolean;
  error: string | null;
  canUsePriceLock: boolean;
  refreshCredits: () => Promise<void>;
  deductCredit: (priceLockId?: string) => Promise<boolean>;
  transactions: DealerCreditTransaction[];
  packages: DealerCreditPackage[];
  loadTransactions: () => Promise<void>;
  loadPackages: () => Promise<void>;
}

export function useDealerCredits(dealerId?: string): UseDealerCreditsReturn {
  const [credits, setCredits] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<DealerCreditTransaction[]>([]);
  const [packages, setPackages] = useState<DealerCreditPackage[]>([]);

  const refreshCredits = async () => {
    if (!dealerId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const creditBalance = await DealerCreditService.checkDealerCredits(dealerId);
      setCredits(creditBalance);
    } catch (err) {
      console.error('Error refreshing credits:', err);
      setError('Errore nel caricamento dei crediti');
    } finally {
      setIsLoading(false);
    }
  };

  const deductCredit = async (priceLockId?: string): Promise<boolean> => {
    if (!dealerId) return false;

    try {
      const success = await DealerCreditService.deductCredit(dealerId, priceLockId);
      if (success) {
        await refreshCredits(); // Ricarica il saldo aggiornato
      }
      return success;
    } catch (err) {
      console.error('Error deducting credit:', err);
      setError('Errore nella sottrazione del credito');
      return false;
    }
  };

  const loadTransactions = async () => {
    if (!dealerId) return;

    try {
      const history = await DealerCreditService.getCreditHistory(dealerId);
      setTransactions(history);
    } catch (err) {
      console.error('Error loading transactions:', err);
      setError('Errore nel caricamento dello storico');
    }
  };

  const loadPackages = async () => {
    try {
      const availablePackages = await DealerCreditService.getCreditPackages();
      setPackages(availablePackages);
    } catch (err) {
      console.error('Error loading packages:', err);
      setError('Errore nel caricamento dei pacchetti');
    }
  };

  useEffect(() => {
    refreshCredits();
  }, [dealerId]);

  return {
    credits,
    isLoading,
    error,
    canUsePriceLock: credits >= 1,
    refreshCredits,
    deductCredit,
    transactions,
    packages,
    loadTransactions,
    loadPackages
  };
}
